import { useMemo } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { Currency } from 'common/contracts';

import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, getApiRouteOrMock } from './api.utils';

export function useCurrencies() {
  const axios = useAxios();

  const getCurrencies = useQuery<Currency[]>({
    retry: false,
    queryKey: [EQueryKeys.CURRENCIES],
    queryFn: async () => {
      const response = await axios.get(
        `${getApiRouteOrMock(EApiRoutes.CURRENCIES)}`
      );

      return response;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const getSupportedCurrencies = useQuery({
    queryKey: [EQueryKeys.SUPPORTED_CURRENCIES],
    queryFn: async () => {
      const response = await axios.get(
        `${getApiRouteOrMock(EApiRoutes.SUPPORTED_CURRENCIES)}`
      );

      return response;
    },
    enabled: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const getDisabledCurrencies = useQuery({
    queryKey: [EQueryKeys.DISABLED_CURRENCIES],
    queryFn: async () => {
      const response = await axios.get(
        `${getApiRouteOrMock(EApiRoutes.DISABLED_CURRENCIES)}`
      );

      return response;
    },
    enabled: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const currencyCodeToCurrencyMap = useMemo(() => {
    return new Map<string, Currency>(
      getCurrencies.data?.map((currency) => [currency.currencyCode, currency])
    );
  }, [getCurrencies.data]);

  const disableCurrencies = useMutation(
    async (disabledCurrencies: string[]) => {
      const route = `${getApiRouteOrMock(EApiRoutes.DISABLE_CURRENCIES)}`;
      return await axios.put(route, { disabledCurrencies });
    },
    {}
  );

  return {
    getCurrencies,
    disableCurrencies,
    currencyCodeToCurrencyMap,
    getSupportedCurrencies,
    getDisabledCurrencies
  };
}
